import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { noop } from "lodash";
import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import TextLink from "@/components/TextLink/TextLink";
import { TEXT_LINK_SIZES } from "@/components/TextLink/types";
import { useAuth } from "@/context/Auth/AuthProvider";
import { useApiCredentialValidation } from "@/Pages/Integrations/IntegrationsApi/hooks/useApiCredentialValidation";
import {
  API_KEY_FIELD_NAMES,
  ApiKeyObjectState
} from "@/Pages/Integrations/types";
import { API_DOCS_URL, ROUTES, useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import useResponse from "@/shared/hooks/useResponse";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import Typography from "@mui/material/Typography";

import ConfirmDeactivateResource from "../../reusableDialogs/ConfirmDeactivateResource/ConfirmDeactivateResource";
import ApiKeyDialogContent from "../ApiKeyDialogContent";

interface AddApiKeyDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddApiKeyDialog = ({ isOpen, onClose }: AddApiKeyDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const { user } = useAuth();
  const { postData } = useAxiosPrivate();
  const { handleCommonError } = useResponse();
  const showGeneratedKey = useIntegrationsDialogStore(
    state => state.showGeneratedKey
  );
  const goBackToTypeSelect = useIntegrationsDialogStore(
    state => state.goBackToTypeSelect
  );

  const initialApiKeyObject: ApiKeyObjectState = {
    [API_KEY_FIELD_NAMES.Name]: "",
    [API_KEY_FIELD_NAMES.SelectedCompany]: user?.selectedCompany ?? null,
    [API_KEY_FIELD_NAMES.ExpirationDate]: null,
    [API_KEY_FIELD_NAMES.Enabled]: true
  };

  const [isLoading, setIsLoading] = useState(false);

  const [hasUpdatedEnable, setHasUpdatedEnable] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);

  const {
    errors,
    setErrors,
    apiKeyObject,
    setApiKeyObject,
    isReadyToConfirm,
    resetAllValues
  } = useApiCredentialValidation(initialApiKeyObject, setHasUpdatedEnable);

  useEffect(() => {
    resetAllValues();
  }, [user?.selectedCompany]);

  const handleSubmit = async (skipConfirm?: boolean) => {
    if (
      !skipConfirm &&
      hasUpdatedEnable &&
      !apiKeyObject[API_KEY_FIELD_NAMES.Enabled]
    ) {
      setIsConfirmDeactivateOpen(true);
      return;
    }
    setIsLoading(true);

    try {
      setIsConfirmDeactivateOpen(false);

      const { name, selectedCompany, expirationDate, enabled } = apiKeyObject;

      const finalPayload = {
        name: name.trim(),
        companyId: selectedCompany?.id,
        expirationDate: expirationDate,
        enabled: enabled
      };

      const { data } = await postData(
        ApiResources.IntegrationsApiCredentials,
        finalPayload
      );

      showGeneratedKey(data?.plainTextApiKey ?? "");
    } catch (error: any) {
      handleCommonError(error, t);
    }

    setIsLoading(false);
  };

  return (
    <>
      <Dialog
        data-testid="add-api-key-dialog"
        title={t("Dialog##generate api key##title")}
        description={
          <Typography variant="body2Medium">
            {t("Dialog##api management##description")}
            <TextLink
              size={TEXT_LINK_SIZES.Normal}
              href={API_DOCS_URL}
              target="_blank"
            >
              {t("General##here")}
            </TextLink>
            {"."}
          </Typography>
        }
        extendToMaxWidth={toMd}
        isTitleSeparator
        isActionsSeparator
        open={isOpen}
        close={isLoading ? noop : onClose}
        submit={handleSubmit}
        width={toMd ? undefined : DialogWidth.MinWidth}
        slotProps={{ transition: { onExited: resetAllValues } }}
        actions={
          <DialogActions
            onClose={goBackToTypeSelect}
            disabledClose={isLoading}
            isLoading={isLoading}
            isreadyToConfirm={isReadyToConfirm}
            confirmationButtonText={t("Button##generate")}
            confirmationButtonTestId="generate-api-key-button"
            cancelButtonText={t("Button##back")}
            cancelButtonTestId="back-api-key-button"
          />
        }
      >
        <ApiKeyDialogContent
          isLoading={isLoading}
          apiKeyObject={apiKeyObject}
          errors={errors}
          hasUpdatedEnable={hasUpdatedEnable}
          setHasUpdatedEnable={setHasUpdatedEnable}
          setErrors={setErrors}
          setApiKeyObject={setApiKeyObject}
        />
      </Dialog>

      <ConfirmDeactivateResource
        isOpen={isConfirmDeactivateOpen}
        route={ROUTES.IntegrationsApi}
        resourceName={apiKeyObject[API_KEY_FIELD_NAMES.Name]}
        onClose={() => setIsConfirmDeactivateOpen(false)}
        onSubmit={() => handleSubmit(true)}
      />
    </>
  );
};

export default AddApiKeyDialog;
